import React, { useState, useEffect } from "react";
import { Button, Tag, Typography, Skeleton, Space, Checkbox } from "antd";
import { SyncOutlined, EyeOutlined } from "@ant-design/icons";
import { fetchOrders } from "../../utils/fetchOrders";
import OrderDrawer from "./OrderDrawer";

const RouteStep1 = ({ routeDay, onComplete }) => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [drawerOrderId, setDrawerOrderId] = useState(null);

  const isH = (order) => String(order.daily_id).toUpperCase().startsWith("H");

  const isUnassigned = (order) => {
    const entry = order.delivery_info_array?.find(
      (d) => d.delivery_date?.startsWith(routeDay)
    );
    return !entry?.driver_id;
  };

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const vals = await fetchOrders(null, routeDay);
      setOrders(vals);
      setSelectedKeys(
        new Set(
          vals.filter((o) => isH(o) || isUnassigned(o)).map((o) => o.order_id)
        )
      );
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [routeDay]);

  const toggle = (orderId) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.has(orderId) ? next.delete(orderId) : next.add(orderId);
      return next;
    });

  const addToSelection = (predicate) =>
    setSelectedKeys((prev) =>
      new Set([
        ...prev,
        ...orders.filter(predicate).map((o) => o.order_id),
      ])
    );

  const hCount = orders.filter(isH).length;
  const unassignedCount = orders.filter(isUnassigned).length;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button icon={<SyncOutlined />} onClick={load} />
        <Button onClick={() => addToSelection(isH)}>
          + Pedidos "H"&nbsp;<Tag>{hCount}</Tag>
        </Button>
        <Button onClick={() => addToSelection(isUnassigned)}>
          + No asignados&nbsp;<Tag>{unassignedCount}</Tag>
        </Button>
        <Button
          onClick={() =>
            setSelectedKeys(new Set(orders.map((o) => o.order_id)))
          }
        >
          Todos
        </Button>
        <Button onClick={() => setSelectedKeys(new Set())}>Limpiar</Button>
        <Typography.Text type="secondary" className="ml-auto">
          {selectedKeys.size} / {orders.length} seleccionados
        </Typography.Text>
      </div>

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : error ? (
        <Typography.Text type="danger">Error al cargar pedidos.</Typography.Text>
      ) : (
        <div
          className="overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg"
          style={{ maxHeight: "44vh" }}
        >
          {orders.map((order) => {
            const checked = selectedKeys.has(order.order_id);
            const deliveries = order.delivery_info_array ?? [];
            const todayDelivery = deliveries.find((d) =>
              d.delivery_date?.startsWith(routeDay)
            );

            return (
              <div
                key={order.order_id}
                onClick={() => toggle(order.order_id)}
                className={`px-4 py-2 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                  checked
                    ? "bg-blue-50 dark:bg-blue-950/30"
                    : "hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <div className="flex justify-between items-start gap-3">
                  {/* Left: checkbox + meta */}
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <Checkbox
                      checked={checked}
                      className="mt-0.5 shrink-0"
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggle(order.order_id)}
                    />
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <Space size={4} wrap>
                        <Tag className="font-mono" style={{ fontSize: 11 }}>{order.daily_id}</Tag>
                        {isH(order) && (
                          <Tag color="blue" style={{ fontSize: 10, padding: "0 4px" }}>H</Tag>
                        )}
                        <Typography.Text strong className="text-sm">{order.name}</Typography.Text>
                      </Space>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }} className="block">
                        {[order.address, order.city?.toUpperCase()].filter(Boolean).join(" — ")}
                      </Typography.Text>
                      {order.products?.length > 0 && (
                        <div className="flex flex-col gap-0.5 mt-0.5">
                          {order.products.map((p, i) => (
                            <Tag key={i} style={{ fontSize: 11, padding: "0 5px", width: "fit-content" }}>
                              {p.quantity}× {p.product_name}{p.variant ? ` (${p.variant})` : ""}
                            </Tag>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Right: driver badge + eye button */}
                  <div
                    className="flex flex-col items-end gap-1 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {todayDelivery?.driver_id ? (
                      <Tag color="green" style={{ fontSize: 11 }}>
                        {todayDelivery.driver_name ?? `ID ${todayDelivery.driver_id}`}
                      </Tag>
                    ) : (
                      <Tag color="orange" style={{ fontSize: 11 }}>No asignado</Tag>
                    )}
                    <Button
                      icon={<EyeOutlined />}
                      size="small"
                      type="text"
                      onClick={() => setDrawerOrderId(order.order_id)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end mt-4">
        <Button
          type="primary"
          disabled={selectedKeys.size === 0}
          onClick={() =>
            onComplete(orders.filter((o) => selectedKeys.has(o.order_id)))
          }
        >
          Continuar ({selectedKeys.size} pedidos)
        </Button>
      </div>

      <OrderDrawer orderId={drawerOrderId} onClose={() => setDrawerOrderId(null)} />
    </div>
  );
};

export default RouteStep1;
